/**
 * React Query Client Configuration
 * Centralized QueryClient with sensible defaults for Firebase data.
 */
import { QueryClient, MutationCache } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { detectarIdioma, traducir } from '../i18n/idioma';

export const queryClient = new QueryClient({
    // Global backstop: surface ANY mutation failure that a call site didn't
    // already handle, so a denied/RLS-blocked write can never fail silently
    // (the report_error / 0-row class of bug). Opt out for mutations that show
    // their own message by setting meta.suppressGlobalError on the mutation.
    mutationCache: new MutationCache({
        onError: (error, _vars, _ctx, mutation) => {
            if (mutation?.meta?.suppressGlobalError) return;
            // Este archivo vive FUERA de React, así que no puede usar el hook
            // `useIdioma`. `detectarIdioma()` lee la misma llave de
            // localStorage que el selector, y `traducir()` es la versión suelta
            // del mismo diccionario: el aviso sale en el idioma elegido en vez
            // de en español fijo dentro de una app puesta en inglés.
            const idioma = detectarIdioma();
            const msg = (error as any)?.message || traducir(idioma, 'comun.errorDesconocido');
            // Stable id collapses a burst of identical failures into one toast.
            toast.error(traducir(idioma, 'errores.guardarCambio', { motivo: msg }), { id: 'global-mutation-error' });
        },
    }),
    defaultOptions: {
        queries: {
            // Data is considered fresh for 2 minutes before a background refetch
            staleTime: 2 * 60 * 1000,
            // Keep unused data in cache for 5 minutes
            gcTime: 5 * 60 * 1000,
            // Retry transient failures twice. Most fetchEstimates / fetchProjects
            // errors in telemetry come from gotrue auth-lock contention or brief
            // Safari "Load failed" blips — both recover within a second on
            // retry, so this knocks out the long tail of singleton fetch errors.
            retry: 2,
            retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
            // Refetch on window focus for real-time-ish feel
            refetchOnWindowFocus: true,
        },
    },
});
